"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"

/**
 * Décompte informatif jusqu'à une date. L'horloge est recalée sur l'heure du
 * serveur ; à l'échéance, la page est rechargée et c'est le serveur qui
 * décide si l'achat est ouvert.
 */
export function Countdown({
  target,
  serverTime,
  label,
  compact = false,
  onEndRefresh = true,
}: {
  target: string
  serverTime: string
  label?: string
  compact?: boolean
  onEndRefresh?: boolean
}) {
  const router = useRouter()
  const offset = useRef<number | null>(null)
  const [now, setNow] = useState<number | null>(null)
  const refreshed = useRef(false)

  useEffect(() => {
    offset.current = new Date(serverTime).getTime() - Date.now()
    const tick = () => setNow(Date.now() + (offset.current ?? 0))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [serverTime])

  const remaining = now === null ? null : Math.max(0, new Date(target).getTime() - now)
  useEffect(() => {
    if (remaining === 0 && onEndRefresh && !refreshed.current) {
      refreshed.current = true
      setTimeout(() => router.refresh(), 800)
    }
  }, [remaining, onEndRefresh, router])

  if (remaining === null) return <span className="tabular-nums">…</span>
  const s = Math.floor(remaining / 1000)
  const parts = [
    { v: Math.floor(s / 86400), l: "j" },
    { v: Math.floor((s % 86400) / 3600), l: "h" },
    { v: Math.floor((s % 3600) / 60), l: "min" },
    { v: s % 60, l: "s" },
  ]
  const shown = parts[0].v > 0 ? parts : parts.slice(1)
  const text = shown.map((p) => `${p.v} ${p.l}`).join(" ")
  if (compact) {
    return (
      <span className="tabular-nums" aria-live="off">
        {text}
      </span>
    )
  }
  return (
    <div className="inline-flex flex-col items-center" aria-label={`${label ?? "Ouverture dans"} ${text}`}>
      {label && <span className="surtitre mb-1">{label}</span>}
      <div className="flex gap-2 sm:gap-3" aria-hidden>
        {shown.map((p) => (
          <span key={p.l} className="papier min-w-[3.6rem] px-2 py-2 text-center">
            <span className="block text-2xl font-bold tabular-nums text-brun">{String(p.v).padStart(2, "0")}</span>
            <span className="block text-xs text-brun-doux">{p.l}</span>
          </span>
        ))}
      </div>
    </div>
  )
}
